import { NextSeo } from "next-seo";
import SimpleFooter from "components/SimpleFooter";
import SimpleNavbar from "components/SimpleNavbar";
import Link from "next/link";

const PrivacyPolicy = () => {
  return (
    <>
      <NextSeo title="Teamshares | Privacy Policy" />
      <div className="min-h-screen antialiased leading-normal text-gray-900 bg-white">
        <SimpleNavbar />
        <div className="max-w-6xl px-12 mx-auto text-sm lg:text-lg">
          <div className="mb-10 text-3xl font-bold pt-28 md:text-4xl">
            Teamshares Inc. California Resident Privacy Notice
          </div>
          <div className="mb-2 font-bold lg:mb-4">
            Last Updated: <span className="font-normal"> 08/14/2023</span>
          </div>
          This California Resident Privacy Notice supplements the information
          and disclosures contained in our{" "}
          <Link href="/privacy-policy" target="_blank" rel="noopener">
            Privacy Policy
          </Link>
          . It applies to individuals residing in California from whom we
          collect Personal Information as a business under the CCPA.
          <div className="mt-2 font-bold underline lg:mt-4">
            Personal Information Collection, Disclosure, and Sale
          </div>
          <div className="mt-2 lg:mt-4"></div>
          Personal Information does not include information that is:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>Lawfully made available from government records.</li>
            <li>Deidentified or aggregated.</li>
            <li>Otherwise excluded from the scope of the CCPA.</li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          The chart below provides the categories of Personal Information and
          Sensitive Personal Information (as defined by the CCPA) we have
          collected, disclosed for a business purpose, or sold or “shared” (for
          cross-context behavioral advertising purposes) in the preceding twelve
          months since this notice was last updated. The examples of Personal
          Information and Sensitive Personal Information provided for each
          category reflect each category’s statutory definition and may not
          reflect all of the specific types of Personal Information and
          Sensitive Personal Information associated with each category.
          <div className="mt-2 lg:mt-4"></div>
          <table className="table-auto border-collapse text-table">
            <thead>
              <tr>
                <th className="border">Category</th>
                <th className="border">We Collected</th>
                <th className="border">We Disclosed</th>
                <th className="border">We Sold/Shared</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <strong>A. Identifiers </strong> <em>Examples: </em>Name,
                  unique personal identifier, online identifier, internet
                  protocol address, email address, account name, social security
                  number, date of birth, or other similar identifiers.&nbsp;
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    B. Categories of Personal Information in Cal. Civ. Code
                    Section 1798.80(e)
                  </strong>{" "}
                  <em>Examples: </em>Name, signature, social security number,
                  physical characteristics or description, address, telephone
                  number, passport number, driver&rsquo;s license or state
                  identification card number, insurance policy number,
                  education, employment, employment history or financial
                  information.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    C. Characteristics of Protected Classifications under
                    California or Federal Law&nbsp;
                  </strong>{" "}
                  <em>Examples: </em>Race or color, ancestry or national origin,
                  religion or creed, age (over 40), mental or physical
                  disability, sex (including gender and pregnancy, childbirth,
                  breastfeeding or related medical conditions), sexual
                  orientation, gender identity or expression, medical condition,
                  genetic information, marital status, military and veteran
                  status.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>D. Commercial Information</strong> <em>Examples: </em>
                  Records of personal property, products or services purchased,
                  obtained, or considered, or other purchasing or consuming
                  histories or tendencies.&nbsp;
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>E. Biometric Information&nbsp;</strong>{" "}
                  <em>Examples: </em>Physiological, biological, or behavioral
                  characteristics, including DNA, that can be used, singly or in
                  combination with each other or with other identifying data, to
                  establish individual identity, such as imagery of the iris,
                  retina, fingerprint, face, hand, palm, vein patterns, and
                  voice recordings, from which an identifier template, such as a
                  faceprint, a minutiae template, or a voiceprint, can be
                  extracted, and keystroke patterns or rhythms, gait patterns or
                  rhythms, and sleep, health, or exercise data that contain
                  identifying information.&nbsp;
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    F. Internet or Other Electronic Network Activity
                    Information&nbsp;
                  </strong>{" "}
                  <em>Examples: </em>Browsing history, search history, and
                  information regarding a consumer&rsquo;s interaction with an
                  internet website, application or advertisement.&nbsp;
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>G. Geolocation Data</strong> <em>Example: </em>Precise
                  physical location.&nbsp;
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>H. Sensory Information</strong> <em>Examples: </em>
                  Audio, electronic, visual, thermal, olfactory, or similar
                  information.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    I. Professional or Employment-Related Information
                  </strong>{" "}
                  <em>Examples: </em>Job application or resume information, past
                  and current job history, and job performance information.
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    J. Non-Public Education Information (as defined in 20 U.S.C.
                    1232g; 34 C.F.R. Part 99)
                  </strong>{" "}
                  <em>Examples: </em>Records that are directly related to a
                  student maintained by an educational agency or institution or
                  by a party acting for the agency or institution.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>K. Inferences Drawn from Personal Information</strong>{" "}
                  <em>Examples: </em>Consumer profiles reflecting a
                  consumer&rsquo;s preferences, characteristics, psychological
                  trends, predispositions, behavior, attitudes, intelligence,
                  abilities, and aptitudes.&nbsp;
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    L. Sensitive Personal Information (Identifiers)
                  </strong>
                  Examples: A consumer&rsquo;s social security, driver&rsquo;s
                  license, state identification card, or passport number.
                </td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    M. Sensitive Personal Information (Log-In and Financial
                    Information)
                  </strong>{" "}
                  <em>Examples: </em>Account log-in, financial account, debit
                  card, or credit card number in combination with any required
                  security or access code, password, or credentials allowing
                  access to an account.
                </td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    N. Sensitive Personal Information (Precise Geolocation)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    O. Sensitive Personal Information (Group Membership)
                  </strong>{" "}
                  <em>Examples: </em>Racial or ethnic origin, religious or
                  philosophical beliefs, or union membership.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    P. Sensitive Personal Information (Contents of
                    Communications)
                  </strong>{" "}
                  <em>Examples: </em>The contents of a consumer&rsquo;s mail,
                  email, and text messages unless the business is the intended
                  recipient of the communication.
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    Q. Sensitive Personal Information (Genetic Data)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    R. Sensitive Personal Information (Identifying Biometric
                    Information)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    S. Sensitive Personal Information (Personal Information
                    Collected and Analyzed Concerning a Consumer&rsquo;s Health)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    T. Sensitive Personal Information (Personal Information
                    Collected and Analyzed Concerning a Consumer&rsquo;s Sex
                    Life or Sexual Orientation)
                  </strong>
                </td>
                <td>No</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
            </tbody>
          </table>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Use of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          We collect your Personal Information for the following specific
          business and commercial purposes:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              <strong>Providing Services</strong>: Providing our services, such
              as providing and delivering products and services to employers
              with respect to their employees (e.g., when an employee receives
              their employee stock grant, and we provide a tax form to their
              employer to facilitate compliance with tax laws) and displaying
              your salary and equity grant information.
            </li>
            <li>
              <strong>Communicating</strong>: Communicating with you, providing
              you with updates and other information relating to our services
              and products, providing information that you request, responding
              to comments and questions, and otherwise providing customer
              support.
            </li>
            <li>
              <strong>Marketing</strong>: Marketing purposes, such as developing
              and providing promotional and advertising materials that may be
              useful, relevant, valuable, or otherwise of interest to you.
            </li>
            <li>
              <strong>Sending Messages</strong>: Sending you email and text
              messages or push notifications.
            </li>
            <li>
              <strong>Deidentification and Aggregation</strong>: Deidentifying
              and aggregating information collected through our services and
              using it for lawful purposes.
            </li>
            <li>
              <strong>Job Applications</strong>: Processing your job
              application.&nbsp;&nbsp;
            </li>
            <li>
              <strong>Safety Issues</strong>: Responding to trust and safety
              issues that may arise.
            </li>
            <li>
              <strong>Compliance</strong>: For compliance purposes, including
              enforcing our Terms of Use or other legal rights, or as may be
              required by applicable laws and regulations or requested by any
              judicial process or governmental agency.
            </li>
            <li>
              <strong>Auditing Interactions</strong>: Auditing related to your
              interaction with our services and concurrent transactions,
              including, but not limited to, counting impressions to unique
              visitors, verifying positioning and quality of impressions, and
              auditing compliance with other standards.
            </li>
            <li>
              <strong>Fraud and Incident Prevention</strong>: Detecting security
              incidents, protecting against malicious, deceptive, fraudulent, or
              illegal activity, and prosecuting those responsible for that
              activity.
            </li>
            <li>
              <strong>Debugging</strong>: Debugging to identify and repair
              errors that impair existing intended functionality.
            </li>
            <li>
              <strong>Transient Use</strong>: Short-term, transient use.
            </li>
            <li>
              <strong>Contracting Vendors</strong>: Contracting with service
              providers to perform services on our behalf or on their behalf,
              including maintaining or servicing accounts, providing customer
              service, processing or fulfilling transactions, verifying customer
              information, processing payments, providing financing, providing
              advertising or marketing services, providing analytics services,
              or providing similar services on behalf of the business or service
              provider.
            </li>
            <li>
              <strong>Research</strong>: Undertaking internal research for
              technological development and demonstration.
            </li>
            <li>
              <strong>Improving Our Services</strong>: Undertaking activities to
              verify or maintain the quality or safety of our services, and to
              improve, upgrade, or enhance our services.
            </li>
            <li>
              <strong>Enabling Transactions</strong>: Otherwise enabling or
              effecting, directly or indirectly, a commercial transaction.
            </li>
            <li>
              <strong>Notified Purpose</strong>: For other purposes for which we
              provide specific notice at the time the information is collected.
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Collection of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          We collect Personal Information from the following categories of
          sources:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              <strong>You/Your Devices</strong>: You or your devices directly.
            </li>
            <li>
              <strong>Users</strong>: Other users of our services.
            </li>
            <li>
              <strong>Affiliates</strong>.
            </li>
            <li>
              <strong>Analytics Providers</strong>.
            </li>
            <li>
              <strong>Partners</strong>: Business partners.
            </li>
            <li>
              <strong>Public</strong>: Publicly accessible sources.&nbsp;
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Disclosure of Personal Information
          </div>
          <div className="mt-2 lg:mt-4"></div>
          In the preceding twelve months since this notice was last updated, we
          disclosed Personal Information to the following categories of third
          parties for the purposes of providing services, communicating,
          marketing, sending messages, deidentification and aggregation, job
          applications, safety issues, compliance, auditing interactions, fraud
          and incident prevention, debugging, transient use, contracting
          vendors, research, improving our services, enabling transactions, and
          for any notified purpose:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              <strong className="block">Affiliates.</strong>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Commercial Information, Internet or Other Electronic Network
              Activity Information, and Professional or Employment-Related
              Information.
            </li>
            <li>
              <strong className="block">Analytics Providers.</strong>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Commercial Information, Internet or Other Electronic Network
              Activity Information, and Professional or Employment-Related
              Information.
            </li>
            <li>
              <span className="block">
                <strong>Vendors</strong>: Vendors and service providers.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Commercial Information, Internet or Other Electronic Network
              Activity Information, and Professional or Employment-Related
              Information.
            </li>
            <li>
              <span className="block">
                <strong>Third Parties as Legally Required</strong>: Third
                parties as required by law and similar disclosures.
              </span>
              Personal Information we disclosed: Identifiers, Categories of
              Personal Information in Cal. Civ. Code Section 1798.80(e),
              Commercial Information, Internet or Other Electronic Network
              Activity Information, and Professional or Employment-Related
              Information.
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Your California Privacy Rights
          </div>
          <div className="mt-2 lg:mt-4"></div>
          If you are a California resident, you may exercise the following
          rights.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Know and Access.</span>
          You may submit a verifiable request for information regarding the: (1)
          categories of Personal Information collected, sold, or disclosed by
          us; (2) purposes for which categories of Personal Information are
          collected or sold by us; (3) categories of sources from which we
          collect Personal Information; (4) categories of third parties to whom
          we disclosed or sold Personal Information; and (5) specific pieces of
          Personal Information we have collected about you during the past
          twelve months.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Delete.</span>
          Subject to certain exceptions, you may submit a verifiable request
          that we delete Personal Information about you that we have collected
          from you.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Correct.</span>
          You may submit a verifiable request that we correct inaccurate
          Personal Information that we maintain about you.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Verification.</span>
          Requests for access to, deletion, or correction of Personal
          Information are subject to our ability to reasonably verify your
          identity in light of the information requested and pursuant to
          relevant CCPA requirements, limitations, and regulations. To verify
          your access, deletion, or correction request, please follow the
          instructions provided in our response to your request.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right to Opt Out.</span>
          In some circumstances, you may opt out of the sale or sharing (for
          cross-context behavioral advertising purposes) of your Personal
          Information. We do not sell or share (for cross-context behavioral
          advertising purposes) your Personal Information.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            Right to Limit Use and Disclosure of Sensitive Personal Information.
          </span>
          In some circumstances, you may limit our use and disclosure of your
          Sensitive Personal Information. We do not use or disclose your
          Sensitive Personal Information for purposes other than the limited
          purposes permitted by the CCPA.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Right of No Retaliation.</span>
          You have the right not to receive discriminatory treatment for the
          exercise of your CCPA privacy rights, subject to certain limitations,
          including if you are an employee, applicant, or independent contractor
          of our business.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Submit Requests.</span>
          To exercise your rights under the CCPA, send an email via our Contact
          Information section below.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Authorizing an Agent.</span>
          If you are acting as an authorized agent to make a request to know,
          delete, correct, or opt out on behalf of a California resident, send
          an email via our Contact Information section below and attach a
          written authorization signed by the resident.
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">Minors</div>
          <div className="mt-2 lg:mt-4"></div>
          We do not knowingly sell or share (for cross-context behavioral
          advertising purposes) Personal Information of consumers under 16 years
          of age. We reserve the right to request proof of age at any stage so
          that we can verify that minors are not using our services.
          <div className="mt-2 lg:mt-4"></div>
          <div className="mt-2 font-bold underline lg:mt-4">
            Updates or Amendments to the California Resident Privacy Notice
          </div>
          <div className="mt-2 lg:mt-4"></div>
          We reserve the right to change this California Resident Privacy Notice
          at any time, so please revisit this page frequently. All changes to
          this California Resident Privacy Notice are effective as of the stated
          “Last Revised and Updated” date, and your continued use of our
          services after the “Last Revised and Updated” date will constitute
          acceptance of, and agreement to be bound by, those changes.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Contact Information.</span>
          <div className="mt-2 lg:mt-4"></div>
          If you have any questions, comments, or concerns about this notice or
          our processing activities please email us at{" "}
          <Link
            href="mailto:support@teamshares.com"
            target="_blank"
            rel="noopener"
          >
            support@teamshares.com
          </Link>{" "}
          or write to us at:
          <div className="mt-2 lg:mt-4"></div>
          Teamshares Inc.
          <br />
          214 Sullivan Street, Suite 6B
          <br />
          NY, NY 10012
        </div>
        <SimpleFooter />
      </div>
    </>
  );
};

export default PrivacyPolicy;
